import { signOutApp } from '@packages/cognito-auth/react';
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useLocation,
  useMatches,
} from '@tanstack/react-router';
import { UserCircle } from 'lucide-react';
import { ActionButton } from '@/components/ActionButton';
import { ModeToggle } from '@/components/ModeToggle';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/cn';
import { adminNavLinks } from '@/routes/admin/adminNavLinks';
import { Breadcrumbs } from '@/routes/admin/Breadcrumbs';

export const Route = createFileRoute('/a')({
  beforeLoad: ({ context }) => {
    // Keep the current URL for signed-out users so they can log in and land on the page they asked for.
    // If they are logged in, but a restaurant user, redirect restaurant root page.
    // This will then redirect them to their restaurant page.
    if (context.convexUser.userType === 'restaurantUser') {
      throw redirect({ to: '/r' });
    }
  },
  component: AdminLayout,
});

function AdminLayout() {
  const { pathname } = useLocation();
  const noPagePadding = useMatches().some((match) => match.staticData.noPagePadding);

  return (
    <SidebarProvider defaultOpen={true}>
      <Sidebar>
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupLabel asChild>
              <Link to={'/'}>
                <span className="text-orange-500 dark:text-orange-400">Super Admin</span>
              </Link>
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {adminNavLinks.map((navLink) => (
                  <SidebarMenuItem key={navLink.to}>
                    <SidebarMenuButton
                      asChild
                      isActive={pathname === navLink.to || pathname.startsWith(`${navLink.to}/`)}
                    >
                      <Link to={navLink.to}>
                        <navLink.icon />
                        <span>{navLink.name}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
      </Sidebar>
      <SidebarInset>
        <header className="flex h-14 lg:h-[60px] items-center gap-4 border-b bg-muted/40 px-4 lg:px-6">
          <SidebarTrigger />
          <Breadcrumbs />
          <div className="w-full flex-1" />
          <ModeToggle />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <ActionButton className="rounded-full" size="icon" variant="secondary">
                <UserCircle className="h-5 w-5" />
                <span className="sr-only">Toggle user menu</span>
              </ActionButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem>Settings</DropdownMenuItem>
              <DropdownMenuItem>Support</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => signOutApp()}>Logout</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className={cn('flex flex-1 flex-col', !noPagePadding && 'gap-4 p-4 lg:gap-6 lg:p-6')}>
          <Outlet />
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
